import React, {
    Children,
    forwardRef,
    isValidElement,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import './BootstrapForm.css';
import LocalizedDateInput from './LocalizedDateInput';
import { SABIT_LKP_EVENT } from '../utils/sabitLkp';
import { currentSabitItems, detectSabitStTur, isGeneralYetkiUser, openSabitDefinitions } from '../utils/sabitDefinitionAccess';

function configComponent(role) {
    const Component = () => null;
    Component.formRole = role;
    return Component;
}

export const Label = configComponent('label');
export const RequiredRule = configComponent('requiredRule');
export const EmptyItem = configComponent('emptyItem');
export const SimpleItem = configComponent('simpleItem');
export const GroupItem = configComponent('groupItem');
export const TabbedItem = configComponent('tabbedItem');
export const TabPanelOptions = configComponent('tabPanelOptions');
export const Tab = configComponent('tab');

function roleChildren(children, role) {
    return Children.toArray(children).filter((child) => isValidElement(child) && child.type?.formRole === role);
}

function roleChild(children, role) {
    return roleChildren(children, role)[0] || null;
}

function resolveExpr(item, expr) {
    if (typeof expr === 'function') return expr(item);
    return expr ? item?.[expr] : item;
}

function normalizeEditorValue(rawValue, editorType, options) {
    if (editorType === 'dxNumberBox') return rawValue === '' ? null : Number(rawValue);
    if (editorType === 'dxCheckBox') return Boolean(rawValue);
    if (editorType === 'dxSelectBox') {
        const items = options.items || options.dataSource || [];
        const match = items.find((item) => String(resolveExpr(item, options.valueExpr)) === String(rawValue));
        return match ? resolveExpr(match, options.valueExpr) : (rawValue === '' ? null : rawValue);
    }
    return rawValue;
}

function sanitizeEditorValue(rawValue, editorType, options) {
    if (editorType === 'dxTextBox' && options.acceptOnlyDigits) {
        return String(rawValue ?? '').replace(/\D/g, '');
    }
    return rawValue;
}

function isPhoneDataField(dataField) {
    return /(^|_)(TEL\d*|GSM\d*|PHONE)($|_)/i.test(String(dataField || ''));
}

function collectRequiredFields(children, fields = []) {
    Children.forEach(children, (child) => {
        if (!isValidElement(child)) return;
        const role = child.type?.formRole;
        if (role === 'simpleItem' && child.props.dataField && roleChild(child.props.children, 'requiredRule')) {
            fields.push({
                dataField: child.props.dataField,
                message: roleChild(child.props.children, 'requiredRule')?.props?.message,
            });
        }
        collectRequiredFields(child.props.children, fields);
    });
    return fields;
}

function BootstrapTabs({ tabs, renderNode, className = '', height }) {
    const [activeIndex, setActiveIndex] = useState(0);
    return (
        <div className={`bootstrap-form__tabs ${className}`} style={{ height: height || undefined }}>
            <div className="nav nav-tabs" role="tablist">
                {tabs.map((tab, index) => (
                    <button key={`${tab.props.title}-${index}`} type="button"
                        className={`nav-link ${activeIndex === index ? 'active' : ''}`}
                        onClick={() => setActiveIndex(index)}>
                        {tab.props.title || `Sekme ${index + 1}`}
                    </button>
                ))}
            </div>
            <div className="bootstrap-form__tab-content">
                {tabs[activeIndex] ? renderNode(tabs[activeIndex].props.children, `tab-${activeIndex}`) : null}
            </div>
        </div>
    );
}

const BootstrapForm = forwardRef(function BootstrapForm({
    children,
    formData = {},
    onFieldDataChanged,
    readOnly = false,
    disabled = false,
    colCount = 1,
    labelLocation = 'top',
    className = '',
    onContentReady,
}, ref) {
    const [, forceRender] = useState(0);
    const [invalidFields, setInvalidFields] = useState([]);
    const formDataRef = useRef(formData || {});
    formDataRef.current = formData || {};
    const requiredFields = useMemo(() => collectRequiredFields(children, []), [children]);

    useEffect(() => {
        const refresh = () => forceRender((tick) => tick + 1);
        window.addEventListener(SABIT_LKP_EVENT, refresh);
        return () => window.removeEventListener(SABIT_LKP_EVENT, refresh);
    }, []);

    const updateData = useCallback((dataField, value) => {
        if (!dataField) return;
        const previousValue = formDataRef.current?.[dataField];
        formDataRef.current[dataField] = value;
        forceRender((tick) => tick + 1);
        setInvalidFields((current) => current.filter((field) => field !== dataField));
        onFieldDataChanged?.({
            dataField,
            value,
            previousValue,
            component: instanceRef.current,
        });
    }, [onFieldDataChanged]);

    const validate = useCallback(() => {
        const brokenRules = requiredFields
            .filter(({ dataField }) => {
                const value = formDataRef.current?.[dataField];
                return value == null || value === '' || value === false;
            })
            .map((rule) => ({
                type: 'required',
                message: rule.message || `${rule.dataField} zorunludur`,
                dataField: rule.dataField,
            }));
        setInvalidFields(brokenRules.map((rule) => rule.dataField));
        return { isValid: brokenRules.length === 0, brokenRules };
    }, [requiredFields]);

    const instanceRef = useRef(null);
    const instance = useMemo(() => ({
        option: (name, value) => {
            if (name !== 'formData') return undefined;
            if (value === undefined) return formDataRef.current;
            formDataRef.current = value || {};
            forceRender((tick) => tick + 1);
            return value;
        },
        updateData,
        validate,
        repaint: () => forceRender((tick) => tick + 1),
        getEditor: () => null,
    }), [updateData, validate]);
    instanceRef.current = instance;
    useImperativeHandle(ref, () => ({ instance }), [instance]);

    const renderEditor = useCallback((itemProps) => {
        const {
            dataField,
            editorType = 'dxTextBox',
            editorOptions = {},
        } = itemProps;
        const value = formDataRef.current?.[dataField];
        const acceptOnlyDigits = editorOptions.acceptOnlyDigits || isPhoneDataField(dataField);
        const lockedByParent = !editorOptions.keepEditable && (readOnly || disabled);
        const editorReadOnly = lockedByParent || editorOptions.readOnly || editorOptions.disabled;
        const editorInstance = {
            option: (name, nextValue) => {
                if (name !== 'value') return undefined;
                if (nextValue === undefined) return formDataRef.current?.[dataField];
                updateData(dataField, nextValue);
                return nextValue;
            },
        };
        const common = {
            id: `bootstrap-form-${dataField}`,
            disabled: Boolean((!editorOptions.keepEditable && disabled) || editorOptions.disabled),
            readOnly: Boolean(editorReadOnly),
            'aria-invalid': invalidFields.includes(dataField),
            className: `${editorType === 'dxCheckBox' ? 'form-check-input' : 'form-control form-control-sm'} ${editorOptions.elementAttr?.class || ''} ${invalidFields.includes(dataField) ? 'is-invalid' : ''}`,
            onKeyDown: (event) => {
                const isShortcut = event.ctrlKey || event.metaKey || event.altKey;
                const isLetterInNumberBox = editorType === 'dxNumberBox' && /^[a-z]$/i.test(event.key);
                const isNonDigit = acceptOnlyDigits && event.key.length === 1 && !/^\d$/.test(event.key);
                if (!isShortcut && (isLetterInNumberBox || isNonDigit)) {
                    event.preventDefault();
                    return;
                }
                editorOptions.onKeyDown?.({ event, component: editorInstance });
                if (event.key === 'Enter') editorOptions.onEnterKey?.({ event, component: editorInstance });
            },
            onBlur: (event) => editorOptions.onFocusOut?.({ event, component: editorInstance }),
        };
        const commit = (rawValue) => {
            if (editorReadOnly) return;
            const sanitizedValue = sanitizeEditorValue(rawValue, editorType, {
                ...editorOptions,
                acceptOnlyDigits,
            });
            const nextValue = normalizeEditorValue(sanitizedValue, editorType, editorOptions);
            updateData(dataField, nextValue);
            editorOptions.onValueChanged?.({
                value: nextValue,
                previousValue: value,
                component: editorInstance,
            });
        };

        if (editorType === 'dxSelectBox') {
            const configuredItems = editorOptions.items || editorOptions.dataSource || [];
            const stTur = editorOptions.lockItems ? null : detectSabitStTur(configuredItems);
            const items = stTur ? currentSabitItems(stTur) : configuredItems;
            return (
                <div className="d-flex align-items-stretch w-100">
                    <select {...common} className={`form-select form-select-sm ${editorOptions.elementAttr?.class || ''} ${invalidFields.includes(dataField) ? 'is-invalid' : ''}`}
                        style={{ flex: 1, minWidth: 0 }} value={value ?? ''} onChange={(event) => commit(event.target.value)}>
                        {(editorOptions.showClearButton || value == null || value === '') && <option value="">{editorOptions.placeholder || 'Seçiniz'}</option>}
                        {items.map((item, index) => {
                            const itemValue = resolveExpr(item, editorOptions.valueExpr);
                            return <option key={`${itemValue}-${index}`} value={itemValue ?? ''} disabled={item?.disabled}>
                                {resolveExpr(item, editorOptions.displayExpr)}
                            </option>;
                        })}
                    </select>
                    {stTur && isGeneralYetkiUser() && <button type="button"
                        title="Sabit tanım ekranı" aria-label="Sabit tanım ekranı"
                        style={{ width: 28, minWidth: 28, padding: 0, marginLeft: 3, background: '#28a745', color: '#000', border: '1px solid #1e7e34', borderRadius: 4, fontWeight: 700 }}
                        onClick={() => openSabitDefinitions(stTur)}>T</button>}
                </div>
            );
        }
        if (editorType === 'dxCheckBox') {
            return <input {...common} type="checkbox" checked={Boolean(value)} onChange={(event) => commit(event.target.checked)} />;
        }
        if (editorType === 'dxTextArea') {
            const rows = editorOptions.rows
                || (editorOptions.height ? Math.max(2, Math.round(editorOptions.height / 28)) : (editorOptions.minHeight ? 1 : 3));
            return <textarea {...common} rows={rows}
                maxLength={editorOptions.maxLength} value={value ?? ''} onChange={(event) => commit(event.target.value)} />;
        }
        if (editorType === 'dxNumberBox') {
            return <input {...common} type="number" step={editorOptions.step || 'any'} min={editorOptions.min} max={editorOptions.max}
                value={value ?? ''} onChange={(event) => commit(event.target.value)} />;
        }
        if (editorType === 'dxDateBox') {
            const withTime = editorOptions.type === 'datetime' || String(editorOptions.displayFormat || '').includes('HH');
            return (
                <LocalizedDateInput
                    {...common}
                    value={value}
                    withTime={withTime}
                    min={editorOptions.min}
                    max={editorOptions.max}
                    onValueChange={commit}
                    onBlur={(event) => editorOptions.onFocusOut?.({ event, component: editorInstance })}
                    onKeyDown={(event) => event.key === 'Enter' && editorOptions.onEnterKey?.({ event, component: editorInstance })}
                />
            );
        }
        const inputType = acceptOnlyDigits && isPhoneDataField(dataField)
            ? 'tel'
            : (['password', 'tel', 'email'].includes(editorOptions.mode) ? editorOptions.mode : 'text');
        return <input {...common} type={inputType}
            inputMode={acceptOnlyDigits ? 'numeric' : editorOptions.inputMode}
            pattern={acceptOnlyDigits ? '[0-9]*' : editorOptions.pattern}
            maxLength={editorOptions.maxLength} placeholder={editorOptions.placeholder}
            value={value ?? ''} onChange={(event) => commit(event.target.value)} />;
    }, [disabled, instance, invalidFields, readOnly, updateData]);

    const renderSimpleItem = useCallback((element, key) => {
        const itemProps = element.props;
        if (itemProps.visible === false) return null;
        const labelElement = roleChild(itemProps.children, 'label');
        const labelConfig = itemProps.label || {};
        const labelVisible = labelConfig.visible !== false;
        const labelText = labelElement?.props?.text || labelConfig.text || itemProps.text || itemProps.dataField || '';
        const customRender = itemProps.render || itemProps.template;
        const customChildren = Children.toArray(itemProps.children).filter((child) => {
            if (!isValidElement(child)) return true;
            return !['label', 'requiredRule'].includes(child.type?.formRole);
        });
        const hasRenderedLabel = labelVisible && Boolean(labelText) && itemProps.editorType !== 'dxCheckBox';
        return (
            <div key={key} className={`bootstrap-form__item ${hasRenderedLabel ? '' : 'bootstrap-form__item--no-label'} ${itemProps.cssClass || ''}`}
                style={{ gridColumn: `span ${itemProps.colSpan || 1}` }}>
                {hasRenderedLabel && (
                    <label className="form-label" htmlFor={`bootstrap-form-${itemProps.dataField}`}>{labelText}</label>
                )}
                <div className="bootstrap-form__editor">
                    {customRender
                        ? customRender({
                            component: instance,
                            dataField: itemProps.dataField,
                            editorOptions: itemProps.editorOptions,
                        })
                        : customChildren.length > 0
                            ? customChildren
                            : renderEditor(itemProps)}
                    {itemProps.editorType === 'dxCheckBox' && labelVisible && labelText && (
                        <label className="form-check-label ms-2" htmlFor={`bootstrap-form-${itemProps.dataField}`}>{labelText}</label>
                    )}
                    {invalidFields.includes(itemProps.dataField) && <div className="invalid-feedback d-block">Bu alan zorunludur.</div>}
                </div>
            </div>
        );
    }, [instance, invalidFields, renderEditor]);

    const renderNode = useCallback((nodeChildren, keyPrefix = 'root') => Children.toArray(nodeChildren).map((element, index) => {
        if (!isValidElement(element)) return element;
        const role = element.type?.formRole;
        const key = `${keyPrefix}-${index}`;
        if (role === 'simpleItem') return renderSimpleItem(element, key);
        if (role === 'emptyItem') {
            return <div key={key} className="bootstrap-form__empty" style={{ gridColumn: `span ${element.props.colSpan || 1}` }} />;
        }
        if (role === 'groupItem') {
            if (element.props.visible === false) return null;
            return (
                <fieldset key={key} className={`bootstrap-form__group ${element.props.cssClass || ''}`}
                    style={{
                        gridTemplateColumns: `repeat(${element.props.colCount || 1}, minmax(0, 1fr))`,
                        gridColumn: `span ${element.props.colSpan || 1}`,
                    }}>
                    {element.props.caption && <legend>{element.props.caption}</legend>}
                    {renderNode(element.props.children, key)}
                </fieldset>
            );
        }
        if (role === 'tabbedItem') {
            const options = roleChild(element.props.children, 'tabPanelOptions')?.props || {};
            const className = [
                element.props.cssClass,
                element.props.elementAttr?.class,
                options.elementAttr?.class,
            ].filter(Boolean).join(' ');
            return (
                <BootstrapTabs
                    key={key}
                    tabs={roleChildren(element.props.children, 'tab')}
                    renderNode={renderNode}
                    className={className}
                    height={options.height}
                />
            );
        }
        if (role === 'tab' || role === 'tabPanelOptions' || role === 'label' || role === 'requiredRule') return null;
        return element;
    }), [renderSimpleItem]);

    const content = renderNode(children);
    useEffect(() => {
        onContentReady?.({ component: instance });
    }, [instance, onContentReady]);

    return (
        <div className={`bootstrap-form ${labelLocation === 'left' ? 'bootstrap-form--labels-left' : ''} ${className}`}
            style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }} aria-disabled={disabled}>
            {content}
        </div>
    );
});

export default BootstrapForm;
